import { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ROUTES } from '@constants/routes';
import { ROLE_LABEL } from '@locales/id/labels';
import type { Me } from '@lib/api';
import { useAlertSound, useDismiss } from '@lib/hooks';
import { CONNECTION } from '@locales/id/messages';
import { Avatar, Icon } from '@ui';

/** Avatar menu: who is signed in, the account page, the alert tone, sign out. Closes on outside press, Escape, navigation. */
export function AccountMenu({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const sound = useAlertSound();
  const wrap = useRef<HTMLDivElement>(null);
  const loc = useLocation();
  useEffect(() => setOpen(false), [loc.pathname]);
  const close = useCallback((escaped: boolean) => {
    setOpen(false);
    if (escaped) wrap.current?.querySelector<HTMLButtonElement>('.menu-trigger')?.focus();
  }, []);
  useDismiss(wrap, open, close);
  return (
    <div className="menu-wrap" ref={wrap}>
      <button type="button" className="menu-trigger" aria-expanded={open} aria-haspopup="true" onClick={() => setOpen((o) => !o)}>
        <span className="menu-name">{ROLE_LABEL[me.role]}</span>
        <Avatar name={me.name} size="nav" />
        <span className="sr-only">Menu akun {me.name}</span>
      </button>
      {open && (
        <div className="menu account-menu">
          <div className="menu-head account-head">
            <Avatar name={me.name} size="lg" />
            <span className="account-who">
              <b title={me.name}>{me.name}</b>
              <span className="account-email" title={me.email}>{me.email}</span>
              <span className="account-role">{ROLE_LABEL[me.role]}</span>
            </span>
          </div>
          <NavLink to={ROUTES.account} className={({ isActive }) => (isActive ? 'active' : '')}>
            <Icon name="user" />
            <span className="menu-item-grow">Akun</span>
            <Icon name="next" size={16} />
          </NavLink>
          <button type="button" className="menu-item" role="switch" aria-checked={sound.on} aria-label="Bunyi peringatan kritis" aria-describedby={sound.locked ? 'sound-locked' : undefined} onClick={() => sound.set(!sound.on)}>
            <Icon name={sound.on ? 'sound' : 'sound-off'} />
            <span className="menu-item-grow">Bunyi peringatan</span>
            <span className="switch-track" aria-hidden="true">
              <span className="switch-thumb" />
            </span>
          </button>
          {sound.locked && (
            <p id="sound-locked" className="menu-note xs">{CONNECTION.soundLocked}</p>
          )}
          <hr className="menu-sep" />
          <button type="button" className="menu-item menu-danger" onClick={onLogout}>
            <Icon name="logout" />
            Keluar
          </button>
        </div>
      )}
    </div>
  );
}
